// @ts-nocheck
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import AddressInfoDisplay from "@insite/content-library/Components/AddressInfoDisplay";
import Button from "@insite/mobius/Button";
import Link from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { addressProps } from "@store/Themes/common/props/address";
import useChangeCustomerWithRedirect from "@store/Framework/Common/Hooks/useChangeCustomerAndRedirect";
import useGetShipToAndBillTo from "@store/Framework/Store/Data/Accounts/Hooks/useGetShipToAndBillTo";
import { getMediaQuery } from "@toolkit/Framework/Common/Utilities/mediaQueries";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";

import React from "react";
import styled, { css } from "styled-components";

const mobileQuery = getMediaQuery({ option: "only", first: true, min: 0, max: 480, last: false });

export const HorizontalRule = styled.hr`
    margin-top: 16px;
    margin-bottom: 0;
    border: 1px solid rgba(74, 74, 74, 0.2);
`;

interface CustomerDetailsModalStyles {
    modal?: ModalPresentationProps;
    typography?: TypographyPresentationProps;
    wrapper?: InjectableCss;
    buttonContainer?: InjectableCss;
    addressInfoWrapper?: InjectableCss;
    pickupLocationTypography?: TypographyPresentationProps;
}

const styles: CustomerDetailsModalStyles = {
    addressInfoWrapper: {
        css: css`
            max-width: 50%;
            ${mobileQuery} {
                max-width: 100%;
                width: 100%;
            }
        `,
    },
    buttonContainer: {
        css: css`
            display: flex;
            justify-content: center;
            width: 100%;
            margin-top: 0;
            padding: ${pxToRem(20)};
            gap: ${pxToRem(32)};
            ${mobileQuery} {
                justify-content: center;
            }
        `,
    },
    typography: {
        css: css`
            && {
                color: #0d2c6b;
                font-weight: 700;
                line-height: 1.375;
                padding-top: 1.25rem;
                padding-bottom: 0.25rem;
            }
        `,
    },

    modal: {
        cssOverrides: {
            modalContent: css`
                padding-block: 0;
            `,
            modalBody: css`
                ${mobileQuery} {
                    max-height: 75vh !important;
                    max-width: 90vw !important;
                }
            `,
        },
    },

    wrapper: {
        css: css`
            display: flex;
            justify-content: space-around;
            ${mobileQuery} {
                flex-direction: column;
            }
        `,
    },
};

interface Props {
    isModalOpen: boolean;
    setIsModalOpen: (isModalOpen: boolean) => void;
    customCss?: string;
}

const CustomerDetailsModal: React.FC<Props> = ({ isModalOpen, setIsModalOpen, customCss }) => {
    const { pickUpWarehouse, fulfillmentMethod } = useAppSelector(state => state.context.session);
    const { billTo, shipTo } = useGetShipToAndBillTo();
    const isPickupOrder = fulfillmentMethod === "PickUp";
    const navigateToChangeCustomerPageWithRedirect = useChangeCustomerWithRedirect();
    if (!billTo || !shipTo) {
        return null;
    }
    const customCssWrapper = {
        css: css`
            ${customCss}
        `,
    };
    return (
        <>
            <Modal
                headline={translate("Customer Billing and Shipping Details")}
                isOpen={isModalOpen}
                handleClose={() => setIsModalOpen(false)}
                {...styles.modal}
                size={isPickupOrder ? 900 : undefined}
            >
                <StyledWrapper {...customCssWrapper}>
                    <StyledWrapper {...styles.wrapper} className="customer-details-address-wrapper">
                        <StyledWrapper {...styles.addressInfoWrapper} className="customer-details-billing-wrapper">
                            <Typography as="h2" {...styles.typography} className="customer-details-billing-title">
                                {translate("Billing Information")}
                            </Typography>
                            <AddressInfoDisplay
                                companyName={`${billTo.companyName} (${billTo.customerNumber})`}
                                address1={billTo.address1}
                                address2={billTo.address2}
                                address3={billTo.address3}
                                address4={billTo.address4}
                                city={billTo.city}
                                postalCode={billTo.postalCode}
                                state={billTo.state?.abbreviation}
                                country={billTo.country?.name}
                            />
                        </StyledWrapper>
                        <StyledWrapper {...styles.addressInfoWrapper} className="customer-details-shipping-wrapper">
                            <Typography as="h2" {...styles.typography} className="customer-details-shipping-title">
                                {translate("Shipping Information")}
                            </Typography>
                            <AddressInfoDisplay
                                companyName={`${shipTo.companyName} (${
                                    shipTo.customerSequence || shipTo.customerNumber
                                })`}
                                address1={shipTo.address1}
                                address2={shipTo.address2}
                                address3={shipTo.address3}
                                address4={shipTo.address4}
                                city={shipTo.city}
                                postalCode={shipTo.postalCode}
                                state={shipTo.state?.abbreviation}
                                country={shipTo.country?.name}
                            />
                        </StyledWrapper>
                        {isPickupOrder && (
                            <StyledWrapper {...styles.addressInfoWrapper} className="customer-details-pickup-wrapper">
                                <Typography as="h2" {...styles.typography} className="customer-details-pickup-title">
                                    {translate("Pickup Location")}
                                </Typography>
                                <Typography
                                    {...styles.pickupLocationTypography}
                                    className="customer-details-pickup-location"
                                >
                                    {pickUpWarehouse?.description || pickUpWarehouse?.name || ""}
                                </Typography>
                                <AddressInfoDisplay
                                    address1={pickUpWarehouse?.address1 || ""}
                                    address2={pickUpWarehouse?.address2 || ""}
                                    city={pickUpWarehouse?.city || ""}
                                    postalCode={pickUpWarehouse?.postalCode || ""}
                                    state={pickUpWarehouse?.state || ""}
                                    phone={pickUpWarehouse?.phone || ""}
                                />
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                    <HorizontalRule className="customer-details-horizontal-rule" />
                    <StyledWrapper {...styles.buttonContainer} className="customer-details-button-container">
                        <Link
                            href={navigateToChangeCustomerPageWithRedirect.linkUrl}
                            className="customer-details-change-link"
                        >
                            <Button onClick={() => setIsModalOpen(false)} className="customer-details-change-button">
                                {translate("Change")}
                            </Button>
                        </Link>
                        <Button
                            onClick={() => setIsModalOpen(false)}
                            variant="secondary"
                            className="customer-details-cancel-button"
                        >
                            {translate("Cancel")}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>
            </Modal>
        </>
    );
};

export default CustomerDetailsModal;
